import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  GlobalStyleState,
  ProductState,
  SectionState,
  SelectedFeatureMapState,
} from "../store/chart";
import debounce from "lodash/debounce";
import {
  DesktopTableBody,
  DesktopTableColumn,
  DesktopTableRow,
  DesktopTableStickyColumn,
} from "./Table";
import styled from "styled-components";
import { Text } from "./Text";
import { Feature, FeatureName } from "./Feature";
import { FontWeight, UIColor } from "../types";
import {
  Comparison,
  ComparisonPlaceholder,
  ProductOverviewComparison,
} from "./Comparison";

type DesktopFeatureSectionProps = {
  className?: string;
  section: SectionState;
  selectedProducts: ProductState[];
  selectedFeatureMap: SelectedFeatureMapState;
  showOnlyDiffs?: boolean;
  legalCopyArray: string[];
  styling: GlobalStyleState;
  backgroundColor?: UIColor;
};

export function DesktopFeatureSection({
  section,
  selectedProducts,
  selectedFeatureMap,
  showOnlyDiffs,
  legalCopyArray,
  styling,
  backgroundColor,
}: DesktopFeatureSectionProps) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleResize = useCallback(() => {
    if (bodyRef.current) {
      const children = Array.from(bodyRef.current.children);
      const height = children.reduce(
        (acc, child) => acc + child.clientHeight,
        0,
      );
      setMaxHeight(height + 100);
    }
  }, []);

  const handleCollapseToggle = useCallback(() => {
    handleResize();
    setCollapsed((collapsed) => !collapsed);
  }, [handleResize]);

  useEffect(() => {
    window.addEventListener("resize", debounce(handleResize, 300));
    window.addEventListener("deviceorientation", debounce(handleResize, 300));
    // handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("deviceorientation", handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    setCollapsed(section.collapsed);
  }, [section.collapsed]);

  return (
    <>
      <DesktopTableBody>
        <DesktopTableRow fluid={selectedProducts.length === 0}>
          <DesktopTableStickyColumn backgroundColor={backgroundColor?.hex}>
            <DesktopFeatureSectionHeader onClick={handleCollapseToggle}>
              <CollapsibleIcon collapsed={collapsed} />
              <DesktopFeatureSectionTitle
                font={section.font}
                textColor={section.desktopTextColor}
              >
                {section.name}
              </DesktopFeatureSectionTitle>
            </DesktopFeatureSectionHeader>
          </DesktopTableStickyColumn>
          {selectedProducts.map((product) => (
            <td key={product.id} />
          ))}
        </DesktopTableRow>
      </DesktopTableBody>
      <DesktopFeatureSectionBody
        ref={bodyRef}
        collapsed={collapsed}
        style={
          collapsed ? { maxHeight: 0 } : maxHeight !== null ? { maxHeight } : {}
        }
      >
        {section.subSections.map((subSection, index) => {
          const filteredFeatures = showOnlyDiffs
            ? subSection.features.filter(
                (feature) => selectedFeatureMap[feature.id].hasDifferences,
              )
            : subSection.features;
          const features = filteredFeatures.map((feature) => (
            <DesktopTableRow
              key={feature.id}
              fluid={selectedProducts.length === 0}
            >
              <DesktopTableStickyColumn backgroundColor={backgroundColor?.hex}>
                <DesktopFeatureColumnHeader>
                  <Feature feature={feature} legalCopyArray={legalCopyArray} />
                </DesktopFeatureColumnHeader>
              </DesktopTableStickyColumn>
              {selectedProducts.map((product) => (
                <DesktopFeatureColumn key={product.id}>
                  <Comparison
                    product={product}
                    productFeatureMap={selectedFeatureMap[feature.id]}
                    legalCopyArray={legalCopyArray}
                    styling={styling}
                  />
                </DesktopFeatureColumn>
              ))}
            </DesktopTableRow>
          ));
          let overview;
          if (
            index === 0 &&
            section.showProductOverview &&
            section.productOverviewTitle
          ) {
            overview = (
              <DesktopTableRow>
                <DesktopTableStickyColumn>
                  <DesktopFeatureColumnHeader>
                    <FeatureName>{section.productOverviewTitle}</FeatureName>
                  </DesktopFeatureColumnHeader>
                </DesktopTableStickyColumn>
                {selectedProducts.map((product) => (
                  <DesktopFeatureColumn key={product.id}>
                    <ComparisonPlaceholder product={product} styling={styling}>
                      <ProductOverviewComparison overview={product.overview} />
                    </ComparisonPlaceholder>
                  </DesktopFeatureColumn>
                ))}
              </DesktopTableRow>
            );
          }
          if (!subSection.name) {
            return overview ? (
              <>
                {overview}
                {features}
              </>
            ) : (
              features
            );
          }
          return (
            <Fragment key={subSection.id}>
              {overview}
              <DesktopTableRow fluid={selectedProducts.length === 0}>
                <DesktopTableStickyColumn
                  backgroundColor={backgroundColor?.hex}
                >
                  <DesktopFeatureSubSectionTitle
                    font={subSection.font}
                    textColor={subSection.desktopTextColor}
                  >
                    {subSection.name}
                  </DesktopFeatureSubSectionTitle>
                </DesktopTableStickyColumn>
                {selectedProducts.map((product) => (
                  <DesktopTableColumn key={product.id}>
                    <ComparisonPlaceholder
                      product={product}
                      styling={styling}
                    />
                  </DesktopTableColumn>
                ))}
              </DesktopTableRow>
              {features}
            </Fragment>
          );
        })}
      </DesktopFeatureSectionBody>
    </>
  );
}

const DesktopFeatureSectionHeader = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.getValue(16)}px;
  cursor: pointer;
  padding: ${({ theme }) =>
    `${theme.getValue(30)}px ${theme.getValue(16)}px ${theme.getValue(
      10,
    )}px 0`};
`;

const DesktopFeatureSectionBody = styled.tbody<{ collapsed?: boolean }>`
  display: block;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  transition: all 0.5s;
`;

const DesktopFeatureSectionTitle = styled(Text)`
  &&& {
    font-size: ${({ theme }) => theme.getFontSize(36)}px;
    line-height: 1;
    margin: 0;
  }
`;

const DesktopFeatureSubSectionTitle = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Black } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(26)}px;
    line-height: 1;
    margin: 0;
    padding: ${({ theme }) =>
      `${theme.getValue(30)}px ${theme.getValue(16)}px ${theme.getValue(
        10,
      )}px ${theme.getValue(44)}px`};
  }
`;

const CollapsibleIcon = styled.div<{ collapsed?: boolean }>`
  position: relative;
  top: -${({ theme }) => theme.getValue(3)}px;
  font-size: ${({ theme }) => theme.getFontSize(28)}px;
  line-height: 1;
  width: ${({ theme }) => theme.getValue(28)}px;
  height: ${({ theme }) => theme.getValue(28)}px;
  border: 0.07em solid ${({ theme }) => theme.colors.black};
  border-radius: 50%;

  &:before {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.07em;
    height: 0.5em;
    content: "";
    transform: ${({ collapsed }) =>
      collapsed
        ? "translate(-50%, -50%)"
        : "translate(-50%, -50%) rotate(90deg)"};
    background: ${({ theme }) => theme.colors.black};
    opacity: ${({ collapsed }) => (collapsed ? 1 : 0)};
    transition: all 0.5s;
  }

  &:after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.5em;
    height: 0.07em;
    content: "";
    transform: translate(-50%, -50%);
    background: ${({ theme }) => theme.colors.black};
  }
`;

const DesktopFeatureColumnHeader = styled.div`
  z-index: 20;
  height: 100%;
  margin-left: ${({ theme }) => `${theme.getValue(44)}px`};
  padding: ${({ theme }) =>
    `${theme.getValue(32)}px ${theme.getValue(16)}px ${theme.getValue(
      32,
    )}px 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
`;

const DesktopFeatureColumn = styled(DesktopTableColumn)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
`;
